/**
 * Ce que « Signaler un problème » joint tout seul (décision 146, R7, R8) :
 * l'écran gabarité, le navigateur, la dernière erreur du navigateur si elle a
 * moins de 5 minutes, la dernière requête en échec. Rien qui désigne un
 * patient : le chemin perd ses identifiants, le message ses matricules.
 */
import { epurerMessageErreur, gabaritEcran, type CreationSignalement } from '@afs/shared'

export const FRAICHEUR_ERREUR_MS = 5 * 60_000

interface Trace {
  readonly message: string
  readonly quand: number
}

let derniereErreur: Trace | null = null
let derniereRequeteEnEchec: string | null = null

export function noterErreur(message: string, quand = Date.now()): void {
  derniereErreur = { message, quand }
}

/** Appelé par le client HTTP : le `requestId` retrouve la ligne des journaux, sans donnée. */
export function noterRequeteEnEchec(requestId: string): void {
  derniereRequeteEnEchec = requestId
}

/** Les erreurs non rattrapées de la page, écoutées dès le chargement de la coque. */
export function ecouterLesErreurs(cible: Window = window): () => void {
  const surErreur = (e: ErrorEvent): void => {
    noterErreur(e.message)
  }
  const surRejet = (e: PromiseRejectionEvent): void => {
    noterErreur(e.reason instanceof Error ? e.reason.message : 'Promesse rejetée')
  }
  cible.addEventListener('error', surErreur)
  cible.addEventListener('unhandledrejection', surRejet)
  return () => {
    cible.removeEventListener('error', surErreur)
    cible.removeEventListener('unhandledrejection', surRejet)
  }
}

/** Le contexte joint, tel qu'il part et tel que la personne le voit avant l'envoi. */
export function contexteJoint(
  lieu: { readonly chemin: string; readonly navigateur: string },
  evenementSuivi: string | null,
  maintenant = Date.now(),
): Omit<CreationSignalement, 'categorie' | 'texte'> {
  const fraiche =
    derniereErreur !== null && maintenant - derniereErreur.quand < FRAICHEUR_ERREUR_MS
      ? derniereErreur
      : null
  return {
    ecran: gabaritEcran(lieu.chemin),
    navigateur: lieu.navigateur.slice(0, 300),
    ...(fraiche === null
      ? {}
      : {
          derniereErreur: {
            evenementId: evenementSuivi,
            message: epurerMessageErreur(fraiche.message),
          },
        }),
    ...(derniereRequeteEnEchec === null ? {} : { requeteIdEchec: derniereRequeteEnEchec }),
  }
}

/** Le navigateur en une ligne : son nom court et la taille de la fenêtre. */
export function decrireNavigateur(agent: string, largeur: number, hauteur: number): string {
  const nom =
    /(Edg|Firefox|Chrome|Safari)\/(\d+)/.exec(agent)?.slice(1, 3).join(' ') ?? 'Navigateur'
  return `${nom.replace('Edg', 'Edge')} · ${String(largeur)}×${String(hauteur)}`
}
